import {Ref, unref} from "vue";
import storage from "@/mw-components/store/storage";
import {STORE_RESPONDED} from "@/assets/constants";
import type { ErrorByElementId } from "./types";
import {SurveyImagePosition} from "@/mw-components/enums/survey";

/** @todo This is a very temporary fix for allowing survey resubmissions */
export const surveysToAllowMultipleResponsesInSameTab = [
  '710c0303-04db-44c0-b56d-91f9815fefd0', // QVH Staff Travel Survey 2023 on Queen Victoria Hospital
  '669d7add-c0b4-4363-9627-3193358d9ebf', // 'test days' on Ricky Test
  'bf774279-f7ac-4ef9-996b-4655a2c9932a',
  '812a84fa-a635-4e0f-afa2-2b77375bbe30',
  // Added for issue #1955
  'c5f0b9fe-d134-411d-afbb-ad88c5870acb',
  'a395138b-043e-44a4-baea-47f0d2482617',
  '10b4c235-4a19-4c04-9637-5bbc7864a7c5',
  '385e7346-d283-43ff-9a90-6c8fd3c5c643',
  '2927d6a3-7039-4c8d-be89-a846bd18df39',
  '8c551d9e-151e-40e0-bc14-2f0eee8d385d',
  'c9cb580a-5469-4da0-82e9-992c2c608128',
  '095562e6-c27b-41e1-aeaf-db7c6a4617c9',
  '143366ee-77cb-44dc-9fd1-4fe073a4035f',
  'ca9ee418-be87-422f-b22e-7a5adfdd75a3',
  'b36a5a08-b1f6-409e-9016-509f58d7784f',
  '60c0804a-0856-4f16-851f-f5cce6795c32',
  '8e46fc86-d85c-4a45-8c18-39f4891aaaef',
  '5ff359c3-dbc2-46ba-9d6b-2d80ed64d75e',
];

export default {

  // Updates the list of surveys in local storage that have been answered
  updateAnsweredSurveys(publicSurveyId: string | Ref<string>, contactId: string | Ref<string>): void {
    let answeredSurveys: string[] | null = storage.get(STORE_RESPONDED);
    // Unref, just in case
    const key = unref(publicSurveyId) + (unref(contactId) ? '/' + unref(contactId) : '');
    // If there are no answered surveys (or if there's something unexpected in there), create an empty array
    if (!answeredSurveys || !Array.isArray(answeredSurveys)) answeredSurveys = [];
    // Then push the key into the storage
    answeredSurveys.push(key);
    storage.set(STORE_RESPONDED, answeredSurveys);
  },

  // Checks if the survey has already been answered (via local storagr)
  checkIfAlreadyAnswered(publicSurveyId: string | Ref<string>, contactId: string | Ref<string>): boolean {
    const answeredSurveys = storage.get(STORE_RESPONDED);
    if (surveysToAllowMultipleResponsesInSameTab.includes(unref(publicSurveyId))) return false;

    if (!answeredSurveys || !Array.isArray(answeredSurveys) || !answeredSurveys.length) {
      // No stored surveys answered, allow them to see it
      return false;
    }
    const key = unref(publicSurveyId) + (unref(contactId) ? '/' + unref(contactId) : '');
    return answeredSurveys.includes(key);
  },

  // Adds an error to the DOM and returns the error object
  addErrorByElementId(errorMessage: string, inputId: string): ErrorByElementId {
    const input = document.querySelector(`#${inputId}`);
    if (input) {
      input.classList.add('border-red-500');
      const newElement = document.createElement('p');
      if (errorMessage) {
        newElement.className = 'text-red-500 text-left font-bold mt-6 mb-1 inlineError';
        newElement.innerText = errorMessage;
      } else {
        newElement.className = 'inlineError';
      }
      newElement.id = `err-${inputId}`;
      input.parentNode?.insertBefore(newElement, input);
    }
    return {
      errorMessage,
      inputId,
    };
  },

  // Scroll to the first error on a page
  scrollToFirstError() {
    //find the first occurance of the inline error class
    const firstError = document.querySelector('.inlineError');
    //if first error exists, scroll to it
    if (firstError && firstError.parentNode !== null) {
      // @ts-expect-error
      firstError.parentNode?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  },

  // Remove a single error from the DOM
  clearSingleError (inputId: string) {
    document.querySelector(`#err-${inputId}`)?.remove();
    document.querySelector(`#${inputId}`)?.classList.remove('border-red-500');
  },

  // Remove all errors from the DOM
  resetErrors() {
    const errors = document.querySelectorAll('.inlineError');
    const errorInputs = document.querySelectorAll('.inlineErrorInput');
    errors.forEach((element) => element.remove());
    errorInputs.forEach((element) => element.classList.remove('border-red-500'));
  },

  // Convert image sizes to CSS styles
  convertImageSizeToCssStyle(
    emailWelcomeImageUrl: string,
    imageWidth: number | null,
    imageHeight: number | null,
  ) {
    const defaultHeight = emailWelcomeImageUrl ? 80 : 130;
    let style = { width: '', height: `${defaultHeight}px` };
    if (imageWidth && imageHeight) {
      style.width = `${imageWidth}px`;
      style.height = `${imageHeight}px`;
    }
    return style;
  },

  // Returns the Tailwind CSS class for the image alignment
  getImageAlignmentClass(imageHorizontalPosition: SurveyImagePosition) {
    let alignment = '';
    switch (imageHorizontalPosition) {
      case SurveyImagePosition.Left:
        alignment = 'justify-start';
        break;
      case SurveyImagePosition.Right:
        alignment = 'justify-end';
        break;
      default:
        alignment = 'justify-center';
        break;
    }
    return alignment;
  },

}



